/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { NgModule } from '@angular/core';
import { Routes, RouterModule, RouteReuseStrategy } from '@angular/router';

import { LegacyRouting } from './services/legacy-helpers/legacy-routing.service';
import { CustomReuseStrategy } from './services/routing/custom-reuse-strategy.service';

import { UserDataResolver } from './services/routing/user-data-resolver.service';
import { NotAuthenticatedGuard } from './services/routing/not-authenticated-guard.service';
import { WaitForFrameGuard } from './services/routing/wait-for-frame-guard.service';
import { UnsubstantiatedRouteExecution } from './services/routing/unsubstantiated-route-execution.service';
import { defaultRouteGuard } from './services/routing/default-route-guard.service';

//route component imports
import {
	AccountComponent,
	AccountProfileComponent,
	PlanComponent,
	HarnessComponent,
	Harness2Component,
	ZoneDebugComponent,
} from './components/routes';
import { EmptyComponent } from './components/routes/empty.component';
import { ProjectAccessGuard } from './services/routing/project-access-guard.service';
import { SignUpPageFeatureFlagGuard } from './services/routing/sign-up-page-feature-flag-guard.service';
import { DevRoutesGuard } from './services/routing/dev-routes-guard.service';
import { ProjectCatalogResolver } from './services/routing/project-catalog-resolver.service';
// BEGIN PLACEHOLDER REPLACE AS PART OF TP-5368
import { mustMigrateToTenantGuard } from 'src/app/common/auth/guards/auth-guards';
import { TestComponent } from './components/routes/test/test.component';
import { projectSettingsGuard } from './services/routing/project-settings-guard.service';
import { clearProjectContextGuard, projectContextGuard } from './services/routing/project-context-guard';
// END PLACEHOLDER REPLACE AS PART OF TP-5368

const routes: Routes = [
	{
		path: 'plan/:projectId/:planId',
		component: PlanComponent,
		data: { noReuse: true }, //custom override, ensure that navigating to a different plan will get a full reload
		canActivate: [
			WaitForFrameGuard,
			UserDataResolver,
			ProjectAccessGuard,
			mustMigrateToTenantGuard,
			projectContextGuard,
		],
		canDeactivate: [clearProjectContextGuard],
		resolve: {
			userData: UserDataResolver,
			projectCatalog: ProjectCatalogResolver,
		},
		// RunGuardsAndResolvers: 'always'
	},
	{
		path: 'projects',
		loadChildren: () => import('./modules/projects2/projects2.module').then((m) => m.Projects2Module),
	},
	{
		path: 'account',
		component: AccountComponent,
		canActivate: [WaitForFrameGuard, UserDataResolver, mustMigrateToTenantGuard],
		resolve: {
			userData: UserDataResolver,
		},
		children: [
			{
				path: '',
				pathMatch: 'full',
				redirectTo: 'profile',
			},
			{
				path: 'profile',
				component: AccountProfileComponent,
			},
		],
	},
	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-5398
	// - clean up the commented out code when removing the components themselves
	// {
	// 	path: 'reset-password',
	// 	component: ResetPasswordComponent,
	// 	canActivate: [WaitForFrameGuard, NotAuthenticatedGuard],
	// },
	{
		path: 'logout',
		component: EmptyComponent,
		canActivate: [UnsubstantiatedRouteExecution],
	},
	{
		path: 'clear',
		component: EmptyComponent,
		canActivate: [UnsubstantiatedRouteExecution],
	},
	// {
	// 	path: 'auth',
	// 	component: EmptyComponent,
	// 	canActivate: [UnsubstantiatedRouteExecution],
	// },
	// END PLACEHOLDER REPLACE AS PART OF TP-5398
	{
		path: 'auth',
		loadChildren: () => import('src/app/common/auth/auth-route-config'),
		canActivate: [UnsubstantiatedRouteExecution],
	},
	{
		path: 'harness',
		component: HarnessComponent,
		canActivate: [WaitForFrameGuard, DevRoutesGuard],
	},
	{
		path: 'harness2',
		component: Harness2Component,
		canActivate: [WaitForFrameGuard, DevRoutesGuard],
	},
	{
		path: 'styleguide',
		loadChildren: () => import('./modules/styleguide/styleguide.module').then((m) => m.StyleguideModule),
		canActivate: [DevRoutesGuard],
	},
	{
		path: 'zone',
		component: ZoneDebugComponent,
		canActivate: [WaitForFrameGuard],
	},
	{
		path: 'test',
		component: TestComponent,
		canActivate: [WaitForFrameGuard],
	},
	{
		path: 'procore-response',
		component: EmptyComponent,
		canActivate: [WaitForFrameGuard, UserDataResolver, UnsubstantiatedRouteExecution],
	},
	{
		path: 'settings/project/:projectId',
		loadChildren: () => import('src/app/modules/project-settings/project-settings-route-config'),
		canActivate: [ProjectAccessGuard, projectSettingsGuard, projectContextGuard],
		canDeactivate: [clearProjectContextGuard],
	},
	{
		path: '**',
		component: EmptyComponent,
		canActivate: [WaitForFrameGuard, defaultRouteGuard],
		data: { placeholder: true },
	},
];

@NgModule({
	imports: [
		RouterModule.forRoot(
			routes,
			//https://medium.com/engineering-on-the-incline/reloading-current-route-on-click-angular-5-1a1bfc740ab2
			{ onSameUrlNavigation: 'reload' } // , enableTracing: true} //debug
			// {useHash: true, onSameUrlNavigation: 'reload', enableTracing: true} //debug
		),
	],
	exports: [RouterModule],
	providers: [
		LegacyRouting,
		UserDataResolver,
		ProjectAccessGuard,
		WaitForFrameGuard,
		NotAuthenticatedGuard,
		SignUpPageFeatureFlagGuard,
		DevRoutesGuard,
		UnsubstantiatedRouteExecution,
		ProjectCatalogResolver,
		{ provide: RouteReuseStrategy, useClass: CustomReuseStrategy },
	],
})
export class AppRoutingModule {}
